import { useMemo } from "react";
import { avatarKleur, cn, initialen } from "@/lib/utils";

interface AvatarProps {
  /** Bron voor achtergrondkleur als er geen foto is (meestal de volle naam). */
  naam: string;
  /** Voornaam om initialen correct af te leiden. */
  voornaam?: string | null;
  /** Achternaam om tussenvoegsels over te slaan. */
  achternaam?: string | null;
  /** Data URL of gewone URL van een profielfoto. */
  avatarUrl?: string | null;
  /** Grootte in Tailwind-spacing (bv "h-9 w-9"). */
  className?: string;
  /** Grootte van de letters (text-[12px] etc). */
  lettergrootte?: string;
  /** Rounded variant — standaard rounded-lg (8px), soms rounded-full. */
  vorm?: "lg" | "xl" | "2xl" | "full";
}

/**
 * Genereert óf een foto-avatar (als `avatarUrl` gevuld is), óf een
 * gekleurde cirkel/tegel met initialen als fallback.
 *
 * De kleur is deterministisch per naam (zelfde gebruiker = zelfde
 * kleur), wat herkenbaarheid in lijsten sterk verhoogt.
 */
export function Avatar({
  naam,
  voornaam,
  achternaam,
  avatarUrl,
  className = "h-9 w-9",
  lettergrootte = "text-[12px]",
  vorm = "lg",
}: AvatarProps) {
  const kleur = useMemo(() => avatarKleur(naam), [naam]);
  const letters = useMemo(() => {
    if (voornaam !== undefined) return initialen(voornaam, achternaam);
    // Fallback: eerste twee tekens van de naam
    return (naam ?? "?").slice(0, 2).toUpperCase();
  }, [voornaam, achternaam, naam]);

  const vormKlasse =
    vorm === "full"
      ? "rounded-full"
      : vorm === "xl"
        ? "rounded-xl"
        : vorm === "2xl"
          ? "rounded-2xl"
          : "rounded-lg";

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt=""
        aria-hidden
        className={cn("object-cover", vormKlasse, className)}
      />
    );
  }

  return (
    <div
      className={cn(
        "grid place-items-center font-semibold flex-shrink-0 select-none",
        vormKlasse,
        lettergrootte,
        className,
      )}
      style={{ background: kleur.bg, color: kleur.fg }}
      aria-hidden
    >
      {letters}
    </div>
  );
}
